import { getClientDeviceId } from './device'
import { resolveApiUrl } from '../config/env'

const TOKEN_KEY = 'kaiyun_web_token'
const USER_KEY = 'kaiyun_web_user'
const REQUEST_TIMEOUT = 12000

export class ApiError extends Error {
  constructor(message, code = -1, status = 0) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
  }
}

export const authStorage = {
  getToken: () => localStorage.getItem(TOKEN_KEY) || '',
  getUser: () => localStorage.getItem(USER_KEY) || '',
  save(auth) {
    localStorage.setItem(TOKEN_KEY, auth.token)
    localStorage.setItem(USER_KEY, auth.username)
    window.dispatchEvent(new Event('app:web-session-changed'))
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    window.dispatchEvent(new Event('app:web-session-changed'))
  },
}

function isAuthenticationFailure(response, payload) {
  return response.status === 401
    || Number(payload?.code) === 10024
    || payload?.errorCode === 'AUTH_SESSION_INVALID'
}

function expireCurrentSession(requestToken) {
  if (authStorage.getToken() !== requestToken) return
  authStorage.clear()
  window.dispatchEvent(new CustomEvent('app:auth-expired'))
}

export async function request(path, options = {}) {
  const { globalLoading = false, timeoutMs = REQUEST_TIMEOUT, ...fetchOptions } = options
  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
  if (globalLoading) {
    window.dispatchEvent(new CustomEvent('app:request-start', { detail: { requestId, path } }))
  }
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs)
  const token = authStorage.getToken()
  const headers = new Headers(fetchOptions.headers)

  headers.set('Accept', 'application/json')
  headers.set('X-Client-Version', '1.0.0')
  headers.set('X-Device-Id', getClientDeviceId())
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (fetchOptions.body !== undefined && !(fetchOptions.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  try {
    const response = await fetch(resolveApiUrl(path), {
      ...fetchOptions,
      cache: fetchOptions.cache || 'no-store',
      headers,
      body: fetchOptions.body === undefined || fetchOptions.body instanceof FormData
        ? fetchOptions.body
        : JSON.stringify(fetchOptions.body),
      signal: controller.signal,
    })
    const payload = await response.json().catch(() => null)
    if (isAuthenticationFailure(response, payload)) {
      expireCurrentSession(token)
    }
    if (!response.ok) throw Object.assign(new ApiError(payload?.message || `请求失败（${response.status}）`, payload?.code, response.status), { errorCode: payload?.errorCode, recoveryKind: payload?.recoveryKind })
    if (!payload || typeof payload.code !== 'number') return payload
    if (payload.code !== 0) {
      throw Object.assign(new ApiError(payload.message || '请求失败', payload.code, response.status), { errorCode: payload.errorCode, recoveryKind: payload.recoveryKind })
    }
    return payload.result
  } catch (error) {
    if (error?.name === 'AbortError') throw new ApiError('请求超时，请稍后重试')
    if (error instanceof ApiError) throw error
    throw new ApiError('无法连接服务器，请检查后端服务是否已启动')
  } finally {
    window.clearTimeout(timer)
    if (globalLoading) {
      window.dispatchEvent(new CustomEvent('app:request-end', { detail: { requestId, path } }))
    }
  }
}

export const get = (path) => request(path)
export const post = (path, body = {}, options = {}) => request(path, { ...options, method: 'POST', body })
export const put = (path, body = {}) => request(path, { method: 'PUT', body })
export const del = (path) => request(path, { method: 'DELETE' })
export const upload = (path, formData, method = 'POST') => request(path, { method, body: formData })
